import React, { useCallback } from 'react';

import { StandardButton } from '@eika/button';
import { ErrorCard } from '@eika/card';
import { IconTypes } from '@eika/icon';

import { useInvalidateCardQuery } from '@/react-query/hooks/query/use-card';

import { MobilePaymentStatus, PaymentApprovalError } from '@domain/signing';
import { KortbetjeningText } from '@hooks/use-text';
import Message from '@utils/message/Message';
import { MessageType } from '@utils/message/types';

import './TransferResult.scss';

const serverErrorStatuses: MobilePaymentStatus[] = ['UNKNOWN', 'DECLINED', 'CANCELED', 'REJECTED'];

const isServerErrorStatus = (status: MobilePaymentStatus, error: PaymentApprovalError | undefined): boolean =>
    serverErrorStatuses.includes(status) || !!error;

const getPaymentErrorDescription = (
    error: PaymentApprovalError | undefined,
    text: KortbetjeningText,
): string | undefined => {
    if (error) {
        switch (error) {
            case 'DELINQUENT_ACCOUNT':
                return text.paymentDelinquentAccountError;
            default:
        }
    }

    return text.transferDeclinedText;
};

interface TransferResultProps {
    status: MobilePaymentStatus;
    redirectTransactions: () => void;
    text: KortbetjeningText;
    paymentError: PaymentApprovalError | undefined;
    type: 'transfer' | 'payment';
}

export const TransferResult: React.FC<TransferResultProps> = ({
    status,
    redirectTransactions,
    text,
    paymentError,
    type,
}) => {
    const invalidateCardQuery = useInvalidateCardQuery();

    const onClickSuccess = useCallback(() => {
        invalidateCardQuery();
        redirectTransactions();
    }, [invalidateCardQuery, redirectTransactions]);

    if (isServerErrorStatus(status, paymentError)) {
        return (
            <div className="transfer-result">
                <ErrorCard
                    isModal
                    title={text.messageErrorUps}
                    description={getPaymentErrorDescription(paymentError, text)}
                    icon={IconTypes.WALLET_QUESTIONMARK}
                >
                    <StandardButton onClick={redirectTransactions}>{text.transferDeclinedLinkText}</StandardButton>
                </ErrorCard>
            </div>
        );
    }

    return (
        <div className="transfer-result">
            <Message
                type={MessageType.SUCCESS_WITH_CHECKED}
                title={text[`${type}SuccessHeader`]}
                text={text[`${type}SuccessText`]}
            >
                <StandardButton onClick={onClickSuccess}>{text.formBack}</StandardButton>
            </Message>
        </div>
    );
};
